/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2019 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/
import './Login.css';
import AdobeClearSpace from '@exc/brand-icons/AdobeClearSpace';
import AdobeExperienceCloud from '@exc/brand-icons/ExperienceCloudColor';
import {AuthInstance, getBestSupportedLocale} from '@exc/auth';
import Button from '@react/react-spectrum/Button';
import {FormattedMessage, withLocalization} from '@adobe/adobe-intl-react';
import {GraphQlService} from '@exc/graphql';
import {history} from '@exc/router';
import Icon from '@react/react-spectrum/Icon';
import Metrics from '@exc/metrics';
import PropTypes from 'prop-types';
import querystring from 'querystring';
import React from 'react';
import Wait from '@react/react-spectrum/Wait';

class Login extends React.Component {
  constructor(props) {
    super(props);
    this.environment = props.environment;
    this.handleSignInWithId = this.handleSignInWithId.bind(this);
    this.year = new Date().getFullYear();
    this.state = {
      loading: true
    };
    this.history = props.history || history;
    this.metrics = new Metrics('exc.core.modules.Login');
  }

  async componentDidMount() {
    document.title = 'Login | Adobe Experience Cloud';
    this.auth = await AuthInstance();

    if (!this.auth.isAuthenticated()) {
      this.setState({loading: false});
      this.metrics.event('Login.imsSSO.ready');
    } else {
      if (this.auth.isValidSession()) {
        const loginTimer = this.metrics.start('Login');
        await this.imsSSOhandler();
        loginTimer.time('done');
      } else {
        this.setState({loading: false});
        this.auth.signOut();
        this.metrics.event('Login.imsSSO.ready', 'Invalid session: imsSSO login required');
      }
    }
  }

  /**
   * Async method to perform IMS SSO and update IMS Session data
   * with Current Org.
   * returns Landing Page and Current Org in response.
   */
  async imsSSOhandler() {
    const {redirectlocation} = this.props.query;

    // Get encoded hash from previous session in order to correctly redirect the user
    let oldHash;
    const {old_hash} = (redirectlocation && querystring.parse(redirectlocation)) || {};
    if (old_hash) {
      oldHash = `#/old_hash=${encodeURIComponent(old_hash)}`;
    }

    // Start timer for User Login Handling after getting redirected from SUSI Screen
    let paramRedirectLocation = redirectlocation && decodeURIComponent(redirectlocation);
    const redirectLocationURL = paramRedirectLocation && new URL(paramRedirectLocation);
    const redirectSearch = redirectLocationURL ? redirectLocationURL.search : '';

    //default path '/' handling should be taken care of login-service
    if (
      redirectLocationURL &&
      redirectLocationURL.pathname === '/' &&
      redirectLocationURL.search === '' &&
      (!redirectLocationURL.hash || redirectLocationURL.hash === '#/' || /^\/$/.test(redirectLocationURL.hash))
    ) {
      paramRedirectLocation = null;
    }

    let landingPageResponse = null;
    let shellInitData = null;
    try {
      const imsAccesstokenInfo = await this.auth.fetchAccessTokenInfo();
      const imsAccessToken = imsAccesstokenInfo && imsAccesstokenInfo.access_token;
      const expiryTimeInterval = imsAccesstokenInfo && imsAccesstokenInfo.expires_in;
      const imsUserProfile = this.auth.fetchUserProfile();
      const locale = getBestSupportedLocale(imsUserProfile, this.props.supportedLocales);
      const initConfig = {
        apiKey: this.auth.getClientId(),
        config_version: 0,
        expiryTimeInterval: JSON.stringify(expiryTimeInterval),
        keys: ['lastClickTimestamp', 'lastSeenAnnouncementsTimestamp', 'lastNotificationSeenTimestamp'],
        locale,
        redirectLocation: paramRedirectLocation,
        sessionUrl: imsUserProfile.session,
        userId: imsUserProfile.userId
      };
      const gqlservice = new GraphQlService(this.environment, this.auth.getClientId(), imsAccessToken);
      shellInitData = await gqlservice.initializeShellDataForLogin(initConfig);
      landingPageResponse = shellInitData.imsSSOhandler;
    } catch (error) {
      if (error.status && error.status === 401) {
        return await this.auth.signOut();
      }
      this.metrics.error('Failed to fetch Landing page: ', error);
    }

    if (!shellInitData) {
      this.metrics.error('GQL returned invalid/null response for Shell Init Data');
    }
    this.metrics.event('Login.redirect.ready');

    let landingPageUrlString = landingPageResponse && landingPageResponse.redirectLocation;
    if (this.props.redirectDisabled) {
      if (paramRedirectLocation === null) {
        this.history.push('/');
        this.props.onAuthComplete();
        return;
      } else {
        landingPageUrlString = paramRedirectLocation;
      }
    }

    if (landingPageUrlString) {
      const landingPageUrl = new URL(landingPageUrlString);
      // Identify if the redirect location is a route in exc-app or an external page.
      // Check to be improved further. Currently validating against first level route.
      if (landingPageUrl.origin !== window.location.origin) {
        // If user's landing page is ExC Home, take user to Unified Shell Home
        if (landingPageUrl.hostname.startsWith('exc-home') && landingPageUrl.pathname.startsWith('/exc-home/home')) {
          this.history.push({
            hash: oldHash,
            pathname: '/',
            search: redirectSearch
          });
        } else {
          window.location.assign(landingPageUrl);
        }
      } else {
        this.history.push({
          hash: oldHash || landingPageUrl.hash,
          pathname: '/',
          search: decodeURIComponent(landingPageUrl.search).split('#')[0]
        });
      }
    } else {
      this.history.push({pathname: '/', search: redirectSearch});
    }
    this.props.onAuthComplete();
  }

  handleSignInWithId() {
    if (!this.state.loading) {
      let imsRedirectUri = `${window.location.origin}/login`;
      if (this.props.querystring) {
        imsRedirectUri += `?${this.props.querystring}`;
      }
      this.auth.signIn(imsRedirectUri);
    }
  }

  // Display login page to sign-in or when the login route is explicitly hit
  render() {
    const {formatMessage} = this.props;

    // In case of an authenticated req & landed from a referrerUri,
    // show loader alone
    const showLoader = document.referrer;
    let waitLoader = <div />;

    if (this.state.loading) {
      waitLoader = <Wait centered className="login-wait-container" size="L" />;
      if (showLoader) {
        return <div style={{height: '100vh'}}>{waitLoader}</div>;
      }
    }

    const icon = <Icon className="login-expcloud-icon" size="L"><AdobeExperienceCloud /></Icon>;
    return (
      <main>
        {waitLoader}
        <div className="login-background" />
        <Icon className="login-adobe-logo"><AdobeClearSpace /></Icon>
        <div className="login-box">
          <div className="login-leftbox">
            {icon}
            <h1 className="login-title">Adobe Experience Cloud</h1>
            <div className="login-sign-in">
              <Button
                className="login-sign-in-button"
                label={formatMessage({
                  defaultMessage: 'Sign In with an Adobe ID',
                  description: 'Label for button to sign in with Adobe ID',
                  messageId: 'signInWithId'
                })}
                onClick={this.handleSignInWithId}
                variant="cta"
              />
            </div>
          </div>
        </div>
        <ul className="login-usage-box">
          <li className="login-left-li">
            <a className="login-link" href="http://www.adobe.com/go/marketingcloud_privacy">
              <FormattedMessage
                defaultMessage="Privacy (UPDATED)"
                description="Label for link to privacy information"
                messageId="privacy"
              />
            </a>
          </li>
          <li className="login-left-li">
            <a className="login-link" href="https://marketing.adobe.com/resources/help/en_US/terms.html">
              <FormattedMessage
                defaultMessage="Terms of Use"
                description="Label for link to Terms of Use"
                messageId="termsOfUse"
              />
            </a>
          </li>
          <li className="login-left-li">
            <a className="login-link" href="http://www.adobe.com/in/privacy/cookies.html">
              <FormattedMessage
                defaultMessage="Cookies"
                description="Label for link to cookies information"
                messageId="cookies"
              />
            </a>
          </li>
          <li className="login-left-li">
            <a className="login-link" href="https://helpx.adobe.com/marketing-cloud.html">
              <FormattedMessage
                defaultMessage="Help"
                description="Label for link to help"
                messageId="help"
              />
            </a>
          </li>
          <li className="login-right-li">{`© ${this.year} Adobe Systems Incorporated. All Rights Reserved.`}</li>
        </ul>
      </main>
    );
  }
}

Login.defaultProps = {
  onAuthComplete: function() {}
};

Login.propTypes = {
  onAuthComplete: PropTypes.func,
  supportedLocales: PropTypes.arrayOf(PropTypes.string)
};

export default withLocalization(Login);
